import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Sparkles, ExternalLink, Code2 } from "lucide-react";

const CopyableBlock = ({ label, code, testId }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — long-press to select instead");
    }
  };
  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-2xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/10">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-white/55 font-semibold">
          <Code2 size={13} />
          {label}
        </div>
        <button
          data-testid={`copy-${testId}-btn`}
          onClick={copy}
          className="flex items-center gap-1.5 text-xs font-semibold bg-white text-black rounded-full px-3 py-1.5 hover:scale-[1.02] transition"
        >
          <Copy size={12} />
          {copied ? "Copied!" : "Copy"}
        </button>
      </div>
      <pre className="p-4 text-[12px] text-white/80 overflow-x-auto font-mono leading-relaxed">
        {code}
      </pre>
    </div>
  );
};

export default function ArtistBadge() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    api
      .get(`/badge/embed/${user.user_id}`)
      .then((r) => {
        if (!cancelled) setData(r.data);
      })
      .catch((e) => {
        if (!cancelled) {
          toast.error(e?.response?.data?.detail || "Couldn't load badge");
        }
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (!user) {
    return (
      <div
        data-testid="badge-signin-required"
        className="px-4 sm:px-8 py-16 max-w-xl mx-auto text-center"
      >
        <Sparkles size={40} className="mx-auto text-[#B580FF] mb-3" />
        <h1 className="font-display text-3xl font-semibold mb-2">
          Sign in to grab your 1o1sound badge
        </h1>
        <p className="text-white/55 text-sm">
          Paste it into your Instagram bio, SoundCloud page, or anywhere else fans find you.
        </p>
      </div>
    );
  }

  if (loading) {
    return <div className="px-4 sm:px-8 py-16 text-white/40">Loading your badge…</div>;
  }
  if (!data) {
    return (
      <div className="px-4 sm:px-8 py-16 text-white/40">Couldn&apos;t load your badge.</div>
    );
  }

  return (
    <div
      data-testid="artist-badge-page"
      className="px-4 sm:px-8 py-10 lg:py-14 max-w-3xl mx-auto"
    >
      <header className="mb-10">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={14} className="text-[#B580FF]" />
          <span className="text-[10px] uppercase tracking-[0.28em] text-white/40">
            artist tools
          </span>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight">
          Your 1o1sound badge
        </h1>
        <p className="text-white/55 text-base sm:text-lg mt-3 max-w-xl">
          A live, self-updating widget that shows your current stream count on 1o1sound.
          Paste it into your Instagram bio, SoundCloud, YouTube description, Linktree, or
          personal site — every render pulls fresh data.
        </p>
      </header>

      {/* Live preview */}
      <section className="mb-10">
        <div className="text-xs uppercase tracking-[0.18em] text-white/55 font-semibold mb-3">
          Live preview
        </div>
        <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-[#1a1a1a] to-[#050505] p-8 flex justify-center">
          <a
            data-testid="badge-preview-link"
            href={data.profile_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block hover:scale-[1.02] transition-transform"
          >
            <img
              src={data.badge_url}
              alt={`${data.name} on 1o1sound`}
              width={360}
              height={84}
              style={{ display: "block", border: 0 }}
            />
          </a>
        </div>
        <div className="text-center text-xs text-white/40 mt-3">
          {data.total_streams.toLocaleString()} stream{data.total_streams === 1 ? "" : "s"} ·
          Updates automatically every 5 minutes
        </div>
      </section>

      {/* HTML snippet */}
      <section className="mb-6">
        <div className="text-xs uppercase tracking-[0.18em] text-white/55 font-semibold mb-3">
          HTML embed (most sites — Linktree, Carrd, blogs)
        </div>
        <CopyableBlock label="HTML" code={data.html_snippet} testId="html" />
      </section>

      {/* Markdown snippet */}
      <section className="mb-6">
        <div className="text-xs uppercase tracking-[0.18em] text-white/55 font-semibold mb-3">
          Markdown (GitHub, Notion, Bear, Obsidian)
        </div>
        <CopyableBlock label="Markdown" code={data.markdown_snippet} testId="markdown" />
      </section>

      {/* Direct URL */}
      <section className="mb-10">
        <div className="text-xs uppercase tracking-[0.18em] text-white/55 font-semibold mb-3">
          Direct image URL (Instagram bio — copy & paste as link sticker)
        </div>
        <CopyableBlock label="URL" code={data.badge_url} testId="url" />
      </section>

      {/* How to use */}
      <section className="rounded-3xl border border-white/10 bg-white/[0.03] p-7">
        <h2 className="font-display text-xl font-semibold mb-3">Where to paste this</h2>
        <ul className="space-y-3 text-sm text-white/70">
          <li className="flex items-start gap-3">
            <ExternalLink size={14} className="text-[#B580FF] mt-0.5 shrink-0" />
            <div>
              <b className="text-white">Instagram bio link:</b> Add it to your Linktree / Beacons /
              Carrd link-in-bio page — fans tap it and land on your 1o1sound profile.
            </div>
          </li>
          <li className="flex items-start gap-3">
            <ExternalLink size={14} className="text-[#B580FF] mt-0.5 shrink-0" />
            <div>
              <b className="text-white">YouTube video descriptions:</b> Drop the markdown link
              under every track upload — viewers convert into listeners.
            </div>
          </li>
          <li className="flex items-start gap-3">
            <ExternalLink size={14} className="text-[#B580FF] mt-0.5 shrink-0" />
            <div>
              <b className="text-white">SoundCloud / Bandcamp profile:</b> Paste the HTML in your
              bio. Cross-promote without competing.
            </div>
          </li>
          <li className="flex items-start gap-3">
            <ExternalLink size={14} className="text-[#B580FF] mt-0.5 shrink-0" />
            <div>
              <b className="text-white">Personal site or press kit:</b> A live, ever-updating stat
              that proves momentum to journalists and A&R reps.
            </div>
          </li>
        </ul>
      </section>
    </div>
  );
}
